// ABOUTME: Setup panel general view for super admin configuration
// ABOUTME: Manages customer-level settings (name, isFulfilment) and global integration settings
import { Box, useTheme } from "@mui/material";
import {
  useEffect,
  useState,
  useRef,
  useCallback,
} from "react";
import { tokens } from "../../../../../../theme";
import { useTranslation } from "react-i18next";
import Subheading from "../../../../../../components/theme/text/Subheading";
import Text from "../../../../../../components/theme/text/Text";
import ShSwitch from "../../../../../../components/theme/inputs/ShSwitch";
import TextInput from "../../../../../../components/theme/inputs/TextInput";
import BoolSetting from "../../../../../../components/global/Settings/BoolSetting";
import StringSetting from "../../../../../../components/global/Settings/StringSetting";
import { useGetSettingsQuery } from "../../../../../../newapi/global/generalApiSlice";
import { useGetCustomersQuery, useUpdateCustomerMutation } from "../../../../../../newapi/user/userSlice";
import { useSelector } from "react-redux";
import { selectCustomers } from "../../../../../../newapi/user/authentication/authSlice";
import { selectEnvironmentid } from "../../../../../../newapi/global/settingsSlice";

const SetupPanelBasicSetup = ( props ) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const customerId = useSelector(selectEnvironmentid);
  const authCustomers = useSelector(selectCustomers);
  const { data: fetchedCustomers } = useGetCustomersQuery();
  const [updateCustomer] = useUpdateCustomerMutation();

  const customersList = Array.isArray(fetchedCustomers) ? fetchedCustomers
    : Array.isArray(authCustomers) ? authCustomers
    : [];
  const currentCustomer = customersList.find(c => c.id === customerId);

  const [customerName, setCustomerName] = useState('');
  const [isFulfilment, setIsFulfilment] = useState(false);
  const nameDebounceRef = useRef(null);

  useEffect(() => {
    if (currentCustomer) {
      setCustomerName(currentCustomer.name ?? '');
      setIsFulfilment(currentCustomer.isFulfilment ?? false);
    }
  }, [currentCustomer]);

  const handleNameChange = useCallback((e) => {
    const newName = e.target.value;
    setCustomerName(newName);
    if (nameDebounceRef.current) clearTimeout(nameDebounceRef.current);
    nameDebounceRef.current = setTimeout(() => {
      updateCustomer({ id: customerId, name: newName });
    }, 500);
  }, [customerId, updateCustomer]);

  useEffect(() => {
    return () => {
      if (nameDebounceRef.current) clearTimeout(nameDebounceRef.current);
    };
  }, []);

  const handleIsFulfilmentChange = async (value) => {
    setIsFulfilment(value);
    await updateCustomer({ id: customerId, isFulfilment: value });
  };

  const { data: dropshippingSettings } = useGetSettingsQuery("dropshipping");
  const { data: printnodeSettings } = useGetSettingsQuery("printnode");
  const { data: bolSettings } = useGetSettingsQuery("bol");
  const { data: etailizeSettings } = useGetSettingsQuery("etailize");
  const { data: amazonSettings } = useGetSettingsQuery("amazon");
  const { data: tiktokSettings } = useGetSettingsQuery("tiktok");
  const { data: allowSettings } = useGetSettingsQuery("allow");
  const { data: shippingSettings } = useGetSettingsQuery("shipping");
  const { data: feedbackSettings } = useGetSettingsQuery("feedback");
  const { data: serviceDeskSettings } = useGetSettingsQuery("servicedesk");
  const { data: onboardingSettings } = useGetSettingsQuery("onboarding_enabled");
  const { data: bulkBarcodePrintSettings } = useGetSettingsQuery("inbound_bulk_barcode_print_enabled");
  const { data: pickupEmailSettings } = useGetSettingsQuery("relation_pickup_email_disabled");

  const allSettings = [
    ...(dropshippingSettings ?? []),
    ...(printnodeSettings ?? []),
    ...(bolSettings ?? []),
    ...(etailizeSettings ?? []),
    ...(amazonSettings ?? []),
    ...(tiktokSettings ?? []),
    ...(allowSettings ?? []),
    ...(shippingSettings ?? []),
    ...(feedbackSettings ?? []),
    ...(serviceDeskSettings ?? []),
    ...(onboardingSettings ?? []),
    ...(bulkBarcodePrintSettings ?? []),
    ...(pickupEmailSettings ?? []),
  ];

  const [etailizeEnabled, setEtailizeEnabled] = useState(false);

  useEffect(() => {
    if (etailizeSettings) {
      const setting = etailizeSettings.find(s => s.key === "etailize_enabled");
      setEtailizeEnabled(setting?.value === "true");
    }
  }, [etailizeSettings]);

  useEffect(() => {
    props.replaceView({
        ...props.config,
    },);

    return () => {};
  }, []);

  return (
    <Box
      sx={{
        height: 1,
        "@media screen and (max-width: 47.9375em)": {
          paddingTop: 0,
          paddingX: 0,
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          width: 1,
          gap: 2,
          overflow: "hidden",
          background:
            theme.palette.mode === "dark"
              ? colors.grey[300]
              : colors.bg["tertiary"],
          paddingX: 2,
          paddingTop: 4,
          height: 1,
          position: "relative",
          borderTopLeftRadius: 24,
          borderTopRightRadius: 24,
          "@media screen and (max-width: 47.9375em)": {
            paddingTop: 0,
            paddingX: 0,
            background: colors.bg["tertiary"],
          },
          "& .carrier": {
            paddingLeft: 0,
            marginRight: 2,
          },
          "& [aria-label='Carrier']": {
            paddingLeft: 0,
            marginRight: 2,
          },
        }}
      >
        <Box
          sx={{
            paddingX: 3,
            "@media screen and (max-width: 47.9375em)": {
              display: "none",
            },
          }}
        >
          <Subheading>{t("core_general_title")}</Subheading>
          {/* <Text light>{t("core_general_subtitle")}</Text> */}
        </Box>

        <Box
          sx={{
            background:
              theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[0],
            flex: 1,
            display:"flex",
            flexDirection:"column",
            gap:3,
            pt:3,
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
            "@media screen and (max-width: 48em)": {
              gap:2.5,
              overflow:"auto",
              pb:11,
              background:
                theme.palette.mode === "dark"
                  ? colors.grey[200]
                  : colors.grey[0],
            },
          }}
        >

          {/* Customer name */}
          <Box
            sx={{
              background:
                theme.palette.mode === "dark"
                  ? colors.grey[400]
                  : colors.grey[0],
              px: 3,
              display:"flex",
              flexDirection:"column",
              gap:1,
            }}
          >
            {/* Mobile */}
            <Box
              sx={{
                display:"none",
                "@media screen and (max-width: 47.9375em)": {
                  display:"block",
                },
              }}
            >
              <Box
                sx={{
                  ml:"-12px",
                  mr:"-12px",
                  pl:2,
                  pr:1,
                  display:"flex",
                  py:1,
                  borderRadius:7,
                  gap:2,
                  alignItems:"center",
                  background:theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[0],
                  border: theme.palette.mode === "dark" ? null : `1px solid ${colors.grey[100]}`,
                  boxShadow:theme.palette.mode === "dark" ? null :  `0px 2px 4px ${colors.grey[200]}`,
                }}
              >
                <Text noBreak semibold>{t("customer_name_title")}</Text>
                <Box sx={{ flex:1, maxWidth:300, marginLeft:"auto", backgroundColor: theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[100], borderRadius:4, display:"flex", height:30 }}>
                  <TextInput height={30} value={customerName} onChange={handleNameChange} sx={{ flex:1 }}/>
                </Box>
              </Box>
              <Text light sx={{ pt:1 }}>{t("customer_name_subtitle")}</Text>
            </Box>
            {/* Desktop */}
            <Box
              sx={{
                width:1,
                display:"flex",
                alignItems:"center",
                justifyContent:"space-between",
                gap:4,
                "@media screen and (max-width: 47.9375em)": {
                  display:"none",
                },
              }}
            >
              <Box sx={{ display:"flex", flexDirection:"column" }}>
                <Text semibold>{t("customer_name_title")}</Text>
                <Text light>{t("customer_name_subtitle")}</Text>
              </Box>
              <Box sx={{ flex:1, maxWidth:300, marginLeft:"auto", backgroundColor: theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[100], borderRadius:4, display:"flex", height:30 }}>
                <TextInput height={30} value={customerName} onChange={handleNameChange} sx={{ flex:1 }}/>
              </Box>
            </Box>
          </Box>

          {/* isFulfilment */}
          <Box
            sx={{
              background:
                theme.palette.mode === "dark"
                  ? colors.grey[400]
                  : colors.grey[0],
              px: 3,
              display:"flex",
              flexDirection:"column",
              gap:1,
            }}
          >
            {/* Mobile */}
            <Box
              sx={{
                display:"none",
                "@media screen and (max-width: 47.9375em)": {
                  display:"block",
                },
              }}
            >
              <Box
                sx={{
                  ml:"-12px",
                  mr:"-12px",
                  pl:2,
                  pr:2,
                  display:"flex",
                  borderRadius:7,
                  gap:2,
                  alignItems:"center",
                  justifyContent:"space-between",
                  background:theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[0],
                  border: theme.palette.mode === "dark" ? null : `1px solid ${colors.grey[100]}`,
                  boxShadow:theme.palette.mode === "dark" ? null :  `0px 2px 4px ${colors.grey[200]}`,
                }}
              >
                <Text noBreak semibold>{t("customer_is_fulfilment_title")}</Text>
                <ShSwitch
                  checked={isFulfilment === true || isFulfilment === "true"}
                  onChange={(e) => { e.stopPropagation(); handleIsFulfilmentChange(e.target.checked); }}
                  color={theme.palette.mode === "dark" ? "blue" : "primary"}
                />
              </Box>
              <Text light sx={{ pt:1 }}>{t("customer_is_fulfilment_subtitle")}</Text>
            </Box>
            {/* Desktop */}
            <Box
              sx={{
                width:1,
                display:"flex",
                alignItems:"center",
                justifyContent:"space-between",
                gap:4,
                cursor:"pointer",
                "@media screen and (max-width: 47.9375em)": {
                  display:"none",
                },
              }}
              onClick={() => handleIsFulfilmentChange(!isFulfilment)}
            >
              <Box sx={{ display:"flex", flexDirection:"column", overflow:"hidden" }}>
                <Text semibold>{t("customer_is_fulfilment_title")}</Text>
                <Text light>{t("customer_is_fulfilment_subtitle")}</Text>
              </Box>
              <ShSwitch
                checked={isFulfilment === true || isFulfilment === "true"}
                onChange={(e) => { e.stopPropagation(); handleIsFulfilmentChange(e.target.checked); }}
                color={theme.palette.mode === "dark" ? "blue" : "primary"}
              />
            </Box>
          </Box>

          <Box
            sx={{
              background:
                theme.palette.mode === "dark"
                  ? colors.grey[400]
                  : colors.grey[0],
              px: 3,
              display:"flex",
              flexDirection:"column",
              gap:1,
            }}
          >
            <BoolSetting settings={allSettings} variant={1} skey="dropshipping_enabled"/>
          </Box>

          <Box
            sx={{
              background:
                theme.palette.mode === "dark"
                  ? colors.grey[400]
                  : colors.grey[0],
              px: 3,
              display:"flex",
              flexDirection:"column",
              gap:1,
            }}
          >
            <StringSetting settings={allSettings} skey="printnode_apikey"/>
          </Box>

          <Box
            sx={{
              background:
                theme.palette.mode === "dark"
                  ? colors.grey[400]
                  : colors.grey[0],
              px: 3,
              display:"flex",
              flexDirection:"column",
              gap:1,
            }}
          >
            <BoolSetting settings={allSettings} variant={1} skey="bol_orders_earliest_handover"/>
          </Box>

          <Box
            sx={{
              background:
                theme.palette.mode === "dark"
                  ? colors.grey[400]
                  : colors.grey[0],
              px: 3,
              display:"flex",
              flexDirection:"column",
              gap:1,
            }}
          >
            <BoolSetting
              settings={allSettings}
              variant={1}
              skey="etailize_enabled"
              onChange={(val) => setEtailizeEnabled(val === true || val === "true")}
            />
          </Box>

          {etailizeEnabled && (
            <>
              <Box
                sx={{
                  background:
                    theme.palette.mode === "dark"
                      ? colors.grey[400]
                      : colors.grey[0],
                  px: 3,
                  display:"flex",
                  flexDirection:"column",
                  gap:1,
                }}
              >
                <StringSetting settings={allSettings} skey="etailize_api_key"/>
              </Box>

              <Box
                sx={{
                  background:
                    theme.palette.mode === "dark"
                      ? colors.grey[400]
                      : colors.grey[0],
                  px: 3,
                  display:"flex",
                  flexDirection:"column",
                  gap:1,
                }}
              >
                <StringSetting settings={allSettings} skey="etailize_api_secret"/>
              </Box>

              <Box
                sx={{
                  background:
                    theme.palette.mode === "dark"
                      ? colors.grey[400]
                      : colors.grey[0],
                  px: 3,
                  display:"flex",
                  flexDirection:"column",
                  gap:1,
                }}
              >
                <StringSetting settings={allSettings} skey="etailize_client_id"/>
              </Box>
            </>
          )}

          <Box
            sx={{
              background:
                theme.palette.mode === "dark"
                  ? colors.grey[400]
                  : colors.grey[0],
              px: 3,
              display:"flex",
              flexDirection:"column",
              gap:1,
            }}
          >
            <BoolSetting settings={allSettings} variant={1} skey="amazon_enabled"/>
          </Box>

          <Box
            sx={{
              background:
                theme.palette.mode === "dark"
                  ? colors.grey[400]
                  : colors.grey[0],
              px: 3,
              display:"flex",
              flexDirection:"column",
              gap:1,
            }}
          >
            <BoolSetting settings={allSettings} variant={1} skey="tiktok_enabled"/>
          </Box>

          <Box
            sx={{
              background:
                theme.palette.mode === "dark"
                  ? colors.grey[400]
                  : colors.grey[0],
              px: 3,
              display:"flex",
              flexDirection:"column",
              gap:1,
            }}
          >
            <BoolSetting settings={allSettings} variant={1} skey="allow_track_and_trace_mails"/>
          </Box>

          <Box
            sx={{
              background:
                theme.palette.mode === "dark"
                  ? colors.grey[400]
                  : colors.grey[0],
              px: 3,
              display:"flex",
              flexDirection:"column",
              gap:1,
            }}
          >
            <BoolSetting settings={allSettings} variant={1} skey="shipping_options_enabled"/>
            <BoolSetting settings={allSettings} variant={1} skey="feedback_enabled"/>
          </Box>

          <Box
            sx={{
              background:
                theme.palette.mode === "dark"
                  ? colors.grey[400]
                  : colors.grey[0],
              px: 3,
              display:"flex",
              flexDirection:"column",
              gap:1,
            }}
          >
            <BoolSetting
              settings={allSettings}
              variant={1}
              skey="servicedesk_enabled"
            />
            <BoolSetting
              settings={allSettings}
              variant={1}
              skey="onboarding_enabled"
            />
            <BoolSetting
              settings={allSettings}
              variant={1}
              skey="inbound_bulk_barcode_print_enabled"
            />
            <BoolSetting
              settings={allSettings}
              variant={1}
              skey="relation_pickup_email_disabled"
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default SetupPanelBasicSetup;
